import React, { useState, useEffect } from 'react';
import { Quote, Star, Play } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials?status=active`);
      setTestimonials(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading testimonials:', error);
      setLoading(false);
    }
  };

  const extractYouTubeId = (url) => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : null;
  };

  const videoTestimonials = testimonials.filter(t => t.type === 'video');
  const textTestimonials = testimonials.filter(t => t.type === 'text');

  return (
    <div className="testimonials-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-blue-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">Patient Testimonials</h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Hear from the families who have experienced compassionate care with Dard Satya. 
              Their words inspire us to continue our mission every day.
            </p>
          </div>
        </div>
      </section>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 mt-4">Loading testimonials...</p>
        </div>
      ) : (
        <>
          {/* Video Testimonials */}
          {videoTestimonials.length > 0 && (
            <section className="py-16 bg-white">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                  <h2 className="text-4xl font-bold text-gray-900 mb-4">Video Testimonials</h2>
                  <p className="text-gray-600 text-lg">Watch families share their experiences</p>
                </div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
                  {videoTestimonials.map((testimonial) => {
                    const videoId = extractYouTubeId(testimonial.content);
                    return (
                      <Card key={testimonial.id} className="border-none shadow-lg hover:shadow-xl transition-all">
                        <CardContent className="p-0">
                          {videoId ? (
                            <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-t-lg">
                              <iframe
                                className="absolute top-0 left-0 w-full h-full"
                                src={`https://www.youtube.com/embed/${videoId}`}
                                title={testimonial.patientName}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              ></iframe>
                            </div>
                          ) : (
                            <div className="h-48 bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center rounded-t-lg">
                              <Play size={64} className="text-blue-300" />
                            </div>
                          )}
                          
                          <div className="p-6">
                            <p className="font-semibold text-gray-900 text-lg">{testimonial.patientName}</p>
                            <p className="text-sm text-gray-600 mt-1">{testimonial.date}</p>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          {/* Text Testimonials */}
          {textTestimonials.length > 0 && (
            <section className="py-16 bg-gradient-to-br from-blue-50 to-white">
              <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                  <h2 className="text-4xl font-bold text-gray-900 mb-4">What Families Say</h2>
                  <p className="text-gray-600 text-lg">Words of appreciation and trust</p>
                </div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
                  {textTestimonials.map((testimonial) => (
                    <Card key={testimonial.id} className="border-none shadow-lg hover:shadow-xl transition-all bg-white">
                      <CardContent className="p-8">
                        <Quote className="text-blue-600 mb-4" size={40} />
                        
                        <p className="text-gray-700 leading-relaxed mb-6 italic">
                          "{testimonial.content}"
                        </p>
                        
                        <div className="flex items-center gap-1 mb-4">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} size={16} className="text-yellow-400 fill-current" />
                          ))}
                        </div>
                        
                        <div>
                          <p className="font-semibold text-gray-900">{testimonial.patientName}</p>
                          <p className="text-sm text-gray-600 mt-1">{testimonial.date}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Empty State */}
          {videoTestimonials.length === 0 && textTestimonials.length === 0 && (
            <section className="py-16 bg-white">
              <div className="container mx-auto px-4 text-center">
                <Quote size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 text-lg">No testimonials available yet</p>
              </div>
            </section>
          )}
        </>
      )}

      {/* Call to Action */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-500 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Share Your Experience</h2>
          <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
            Your feedback helps us improve and guides others seeking compassionate palliative care.
          </p>
          <a href="/contact" className="inline-block">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-full font-semibold hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl">
              Leave a Testimonial
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;
